// supervisor-candidate-review-settlement.ts
//
// Code review P3 H1 (2026-10-01): "has the candidate review already run?" is
// answered PER CANDIDATE, not once per mission. The release flow reads the
// review report at `review-staging/<HEAD>`; after CXR-03 withdraws a G3 bound
// to candidate A, a mission whose HEAD moved to B (redirect, follow-up ticket)
// needs B reviewed — a status-only guard said "already ran" for ever, and every
// later `run` parked at `waiting_review_report` (only exit: `archive`).
//
// The reviewed candidate comes from the journal (`candidate_review_finished`,
// written with the promotion — the same SHA the report is staged under), so
// "same candidate ⇒ one review pair" still survives process restarts (the
// original H1/P20 regression: no second session on a second `run`).
import {
  CLEAN_REVIEW_CANDIDATE_TICKET_ID,
  REVIEW_CANDIDATE_TICKET_ID,
} from "./controller-reserved-ticket-ids.js";
import {
  cleanReviewCandidateSessionAlreadyRan,
  reviewCandidateSessionAlreadyRan,
} from "./review-candidate-session.js";
import type { SupervisorTickInput } from "./supervisor-loop-types.js";

export interface CandidateReviewSettlement {
  /** T-9001 needs no session for the current candidate. */
  local: boolean;
  /** T-9002 needs no session for the current candidate. */
  clean: boolean;
  /** Review ids whose `done` was for ANOTHER candidate — dispatched again from `todo`. */
  staleDone: ReadonlySet<string>;
  /** Mission HEAD, read only when some review has a recorded candidate. */
  headSha?: string;
}

/**
 * Only `done` is bound to a candidate: `cancelled`/`superseded` are a person's
 * or a proposal's decision and stay settled whatever HEAD is. A `done` with no
 * recorded candidate (journal written by an older CLI) keeps the old
 * once-per-mission answer, and so does an unreadable HEAD — re-dispatching on
 * a guess could repeat a paid session every tick.
 */
export async function readCandidateReviewSettlement(
  input: Pick<SupervisorTickInput, "store" | "repoDir" | "gitRunner">,
): Promise<CandidateReviewSettlement> {
  const { tickets, candidate_review_sha: reviewed = {} } = input.store.state;
  const recorded = [REVIEW_CANDIDATE_TICKET_ID, CLEAN_REVIEW_CANDIDATE_TICKET_ID].filter(
    (id) => tickets[id] === "done" && reviewed[id] !== undefined,
  );
  const staleDone = new Set<string>();
  let headSha = "";
  if (recorded.length > 0) {
    const head = await input.gitRunner(["rev-parse", "HEAD"], input.repoDir);
    headSha = head.status === 0 ? head.stdout.trim() : "";
    for (const id of recorded) {
      if (headSha !== "" && reviewed[id] !== headSha) staleDone.add(id);
    }
  }
  return {
    local: reviewCandidateSessionAlreadyRan(tickets) && !staleDone.has(REVIEW_CANDIDATE_TICKET_ID),
    clean:
      cleanReviewCandidateSessionAlreadyRan(tickets) &&
      !staleDone.has(CLEAN_REVIEW_CANDIDATE_TICKET_ID),
    staleDone,
    ...(headSha === "" ? {} : { headSha }),
  };
}
